import React, { useEffect, useMemo, useState } from 'react';

import { UseQueryResult } from '@tanstack/react-query';

import { Alert, Box, CircularProgress, Tab, Tabs, Typography } from '@mui/material';

import { RateByGroup, RateGroup } from '~/services/cruises/RateGroupService';

import GroupedRateList from './GroupedRateList';
import RateCodeList from './RateCodeList';

type TabValue = 'grouped' | 'ungrouped';

interface Props {
  ratesByGroupQuery: UseQueryResult<Array<RateByGroup>, Error>;
  rateGroups: Array<RateGroup>;
  shouldFetchRateDetails: boolean;
  vendorId: string | undefined;
}

function RateByGroupTabs({ ratesByGroupQuery, rateGroups, shouldFetchRateDetails, vendorId }: Props) {
  const [selectedTab, setSelectedTab] = useState<TabValue>('ungrouped');
  const { groupedRates, ungroupedRates } = useMemo(() => {
    const rates = ratesByGroupQuery.data ?? [];
    return {
      groupedRates: rates.filter((rate) => rate.currentGroups.length > 0),
      ungroupedRates: rates.filter((rate) => rate.currentGroups.length === 0),
    };
  }, [ratesByGroupQuery.data]);

  useEffect(() => {
    if (!ungroupedRates.length && groupedRates.length) {
      setSelectedTab('grouped');
    }
    if (!groupedRates.length && ungroupedRates.length) {
      setSelectedTab('ungrouped');
    }
  }, [groupedRates.length, ungroupedRates.length]);

  if (ratesByGroupQuery.isLoading) {
    return <CircularProgress size={24} aria-label="Loading rates" />;
  }

  if (ratesByGroupQuery.isError) {
    return <Alert severity="error">Unable to load rates.</Alert>;
  }

  if (!groupedRates.length && !ungroupedRates.length) {
    return <Typography>No rates found.</Typography>;
  }

  const activeTab = selectedTab === 'ungrouped' && !ungroupedRates.length ? 'grouped' : selectedTab;

  return (
    <Box>
      <Tabs value={activeTab} onChange={(_, value: TabValue) => setSelectedTab(value)} sx={{ mb: 2 }}>
        {ungroupedRates.length > 0 && <Tab value="ungrouped" label={`Ungrouped rates (${ungroupedRates.length})`} />}
        {groupedRates.length > 0 && <Tab value="grouped" label={`Grouped rates (${groupedRates.length})`} />}
      </Tabs>
      {activeTab === 'ungrouped' && (
        <RateCodeList
          rates={ungroupedRates}
          rateGroups={rateGroups}
          shouldFetchRateDetails={shouldFetchRateDetails}
          vendorId={vendorId}
        />
      )}
      {activeTab === 'grouped' && <GroupedRateList rates={groupedRates} />}
    </Box>
  );
}

export default RateByGroupTabs;
